import React, { useLayoutEffect, useState } from "react";
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  useFieldArray,
  useWatch,
} from "react-hook-form";
import { ActionMeta, InputProps, components } from "react-select";
import CreatableSelect from "react-select/creatable";
import { LuAlertCircle, LuLayers, LuRuler } from "react-icons/lu";

import { useAppSelector } from "../../app/hooks";
import { getSizeUnit } from "../../features/appSettings/appSettingsSlice";
import { getItemSizeOptions } from "../../features/items/itemsSlice";
import {
  compareSizes,
  emptySizeRow,
  inheritedSizeRows,
  isNumberSize,
  maxItemSizes,
  sameSizeNames,
  sizeKey,
  sizeLabel,
  typedSizeName,
  uniqueSizeNames,
  validCutLength,
} from "../../static/itemSize";
import {
  RateField,
  hasRate,
  processTypes,
  rateField,
  rateLabel,
  rateNumber,
  sanitizeRate,
} from "../../static/processRate";
import { iconMultiSelectStyles } from "../../static/styles";
import { IFormInput } from "../../types/item";
import LengthInput from "../ui/LengthInput";
import RateInput from "../ui/RateInput";
import cx from "../ui/cx";
import {
  fieldErrorTextClass,
  fieldLabelClass,
  formSelectIconClass,
  sizeTableClass,
  sizeTableCommonRowClass,
  sizeTableHeadClass,
  sizeTableRowClass,
  sizeTableSizeCellClass,
  tableHeadCellClass,
} from "../ui/styles";
import ItemRateFields from "./ItemRateFields";

// The item's own sizes, or one sub item row's.
type SizesPath = "sizes" | `sub_items.${number}.sizes`;
type SizeRatePath = `${SizesPath}.${number}.${RateField}`;
type OwnRatePath = RateField | `sub_items.${number}.${RateField}`;
type SizeLengthPath = `${SizesPath}.${number}.cut_length`;
type OwnLengthPath = "cut_length" | `sub_items.${number}.cut_length`;

const cutLengthLabel = "કટીંગ લંબાઈ";
const cutLengthError = "0 થી વધુ લંબાઈ લખો";

const checkboxClass =
  "h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 accent-[#113F67]";

type SizeOption = { value: string; label: string; __isNew__?: boolean };

const toOption = (name: string, unit: string): SizeOption => ({
  value: name,
  label: sizeLabel(name, unit),
});

// The number keypad on phones: a size is typed as a number. Untyped like the shared select styles.
const SizeInput = (props: InputProps) => (
  <components.Input {...props} inputMode="decimal" />
);

// What the menu looks for: the typed number as it is saved, so "096" still finds 96.
const searchText = (input: string) => {
  const number = rateNumber(input);
  return number === null ? "" : String(number);
};

// The rates of the common row, which fills them into every size. It only helps typing and isn't saved.
type CommonRates = Record<RateField, string>;

const emptyCommonRates: CommonRates = {
  cutting_rate: "",
  turning_rate: "",
  vmc_rate: "",
};

type Props = {
  control: Control<IFormInput>;
  getValues: UseFormGetValues<IFormInput>;
  setValue: UseFormSetValue<IFormInput>;
  // "" for the item itself, or "sub_items.{index}." for a sub item row.
  prefix: "" | `sub_items.${number}.`;
  idPrefix: string;
  // False for an item built from sub items: it has no material of its own to cut, so no cut lengths.
  withCutLength?: boolean;
  // False for an item built from sub items: it never goes to cutting, turning or VMC (its sub items do), so it only
  // picks the sizes and has no rates.
  withRates?: boolean;
  // True for a sub item row: it has no size select, since its sizes are the main item's.
  inheritSizes?: boolean;
  // The sub item row above ("sub_items.{index}."), whose cut lengths this row can copy, and that row's number.
  copyFrom?: `sub_items.${number}.`;
  copyFromNumber?: number;
  className?: string;
};

// The sizes below the material, then the rates: the item's own cut length and 3 rates while it has no sizes, or a
// row of them for each size under a common row whose rates go to every size. The cut length is the material cut for
// one piece, in the size unit. Sizes are numbers in that unit, kept in reading order (94 before 120), like the server
// sends them. An item built from sub items only picks the sizes; each sub item has the same ones, with its own cut
// lengths and rates and no select of its own.
const ItemSizeRates = ({
  control,
  getValues,
  setValue,
  prefix,
  idPrefix,
  withCutLength = true,
  withRates = true,
  inheritSizes = false,
  copyFrom,
  copyFromNumber,
  className,
}: Props) => {
  const sizesName = `${prefix}sizes` as SizesPath;
  const sizeUnit = useAppSelector(getSizeUnit);
  // With the cut length, each size row has a narrower rate cell.
  const rateSpan = withCutLength ? "md:col-span-2" : "md:col-span-3";

  // Names are only ever written by insert() or, on a sub item, replace(), so the fields carry them; rates are read
  // through their Controllers.
  const { fields, insert, remove, replace } = useFieldArray({
    control,
    name: sizesName,
  });

  const savedSizes = useAppSelector(getItemSizeOptions);

  const [commonRates, setCommonRates] = useState(emptyCommonRates);

  // The tick that copies the cut lengths of the sub item above. It only helps typing, isn't saved, and starts off, also
  // on edit.
  const [copyAbove, setCopyAbove] = useState(false);

  // A sub item's rows follow the main item's sizes and keep what was typed for each size. A new size takes the
  // table's common rates, like a size picked in the select does.
  const mainSizes = useWatch({
    control,
    name: "sizes",
    disabled: !inheritSizes,
  });
  const mainNames = inheritSizes
    ? (mainSizes ?? []).map((size) => size.name).join("\n")
    : "";

  // Before paint, so a sub item never shows the boxes of an item without sizes for a moment.
  useLayoutEffect(() => {
    if (!inheritSizes) return;

    const current = getValues(sizesName);
    const rows = inheritedSizeRows(
      getValues("sizes"),
      current,
      commonRates,
      sizeUnit
    );
    if (sameSizeNames(current, rows)) return;

    replace(rows);
    // The common row and the copy tick go with the last size.
    if (rows.length === 0) {
      setCommonRates(emptyCommonRates);
      setCopyAbove(false);
    }
  }, [
    inheritSizes,
    mainNames,
    getValues,
    sizesName,
    commonRates,
    sizeUnit,
    replace,
  ]);

  // From the second sub item on, ticking copies the cut lengths of the sub item above: each size takes the length that
  // row has for it, a blank one too. It copies once: the lengths can then be changed, and later changes above aren't
  // copied. Unticking leaves them as they are; ticking again copies again.
  const canCopy = !!copyFrom && withCutLength && fields.length > 0;

  const handleCopyAbove = (checked: boolean) => {
    setCopyAbove(checked);
    if (!checked || !copyFrom) return;

    const above = getValues(`${copyFrom}sizes` as SizesPath) ?? [];

    (getValues(sizesName) ?? []).forEach((size, index) => {
      const source = above.find(
        (row) => sizeKey(row.name, sizeUnit) === sizeKey(size.name, sizeUnit)
      );
      const length = source?.cut_length ?? "";
      const name = `${sizesName}.${index}.cut_length` as SizeLengthPath;

      if (getValues(name) !== length) {
        // A box already showing an error is checked again, as typing in it would.
        setValue(name, length, {
          shouldDirty: true,
          shouldValidate: !!control.getFieldState(name).error,
        });
      }
    });
  };

  const isFull = fields.length >= maxItemSizes;
  const pickedKeys = new Set(fields.map((size) => sizeKey(size.name, sizeUnit)));
  // Only number sizes are offered. A text size from before sizes became numbers stays on the items that have it.
  const options = uniqueSizeNames(
    savedSizes.map((size) => size.name).filter(isNumberSize),
    sizeUnit
  )
    .filter((name) => !pickedKeys.has(sizeKey(name, sizeUnit)))
    .map((name) => toOption(name, sizeUnit));
  const optionKeys = new Set(
    options.map((option) => sizeKey(option.value, sizeUnit))
  );

  const handleChange = (meta: ActionMeta<SizeOption>) => {
    if (meta.action === "select-option" || meta.action === "create-option") {
      // A new size is saved as its number ("096." → "96"); a picked one keeps its saved name.
      const name =
        meta.action === "create-option"
          ? typedSizeName(meta.option?.value ?? "")
          : meta.option?.value ?? "";
      if (!name || isFull || pickedKeys.has(sizeKey(name, sizeUnit))) return;

      const at = fields.findIndex((size) => compareSizes(size.name, name) > 0);
      // It takes the common rates, unless the item has no rates. Focus stays in the select, so the next size can be
      // picked straight away.
      insert(
        at === -1 ? fields.length : at,
        { ...emptySizeRow(name), ...(withRates ? commonRates : {}) },
        { shouldFocus: false }
      );
    } else if (
      meta.action === "remove-value" ||
      meta.action === "pop-value"
    ) {
      const index = fields.findIndex(
        (size) => size.name === meta.removedValue.value
      );
      if (index === -1) return;

      remove(index);
      // The common row goes with the last size.
      if (fields.length === 1) setCommonRates(emptyCommonRates);
    }
    // "clear" and "deselect-option" can't happen: the select isn't clearable and hides picked sizes.
  };

  // A common rate goes to that process on every size, a blank one too. Each size can still be changed on its own.
  const handleCommonRate = (field: RateField, text: string) => {
    setCommonRates((rates) => ({ ...rates, [field]: text }));

    fields.forEach((_size, index) => {
      const name = `${sizesName}.${index}.${field}` as SizeRatePath;
      if (getValues(name) !== text) setValue(name, text, { shouldDirty: true });
    });
  };

  // The item's own rates and cut length that saving will drop. Their boxes are hidden while there are sizes, so
  // they can't change meanwhile.
  const droppedRates =
    withRates && fields.length > 0
      ? processTypes.flatMap((type) => {
          const text = getValues(
            `${prefix}${rateField(type.value)}` as OwnRatePath
          );
          return hasRate(text) ? [`${rateLabel(type)} ${text}`] : [];
        })
      : [];
  const ownLength =
    withRates && fields.length > 0 && withCutLength
      ? getValues(`${prefix}cut_length` as OwnLengthPath)
      : "";
  const dropped = [
    ...droppedRates,
    ...(hasRate(ownLength) ? [`${cutLengthLabel} ${ownLength}`] : []),
  ];
  const droppedWhat =
    droppedRates.length > 0
      ? hasRate(ownLength)
        ? "ભાવ અને લંબાઈ"
        : "ભાવ"
      : "લંબાઈ";

  // The rates an item built from sub items still holds, its own or any size's, which saving drops. Their boxes are
  // hidden meanwhile, so they can't change.
  const droppedMainRates = withRates
    ? []
    : processTypes
        .filter((type) => {
          const field = rateField(type.value);
          return (
            hasRate(getValues(`${prefix}${field}` as OwnRatePath)) ||
            (getValues(sizesName) ?? []).some((size) => hasRate(size[field]))
          );
        })
        .map(rateLabel);

  const droppedNote = dropped.length > 0 && (
    <p className="flex items-center text-sm text-orange-700">
      <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
      સેવ કરતાં સાઈઝ વગરના {droppedWhat} ({dropped.join(", ")}) કાઢી નાખવામાં
      આવશે
    </p>
  );

  // Under the select.
  const selectNote = !withRates ? (
    droppedMainRates.length > 0 ? (
      <p className="flex items-center text-sm text-orange-700">
        <LuAlertCircle className="h-4 w-4 mr-1 flex-shrink-0" />
        સેવ કરતાં મુખ્ય આઈટમ ના ભાવ ({droppedMainRates.join(", ")}) કાઢી
        નાખવામાં આવશે
      </p>
    ) : (
      <p className="text-xs text-gray-500">
        {fields.length > 0
          ? "આ સાઈઝ દરેક સબ આઈટમ માં આવશે, તેની કટીંગ લંબાઈ અને ભાવ દરેક સબ આઈટમ માં દાખલ કરો"
          : `આઈટમ અલગ અલગ સાઈઝ માં બનતી હોય તો સાઈઝ પસંદ કરો અથવા નવી લખો (ફક્ત આંકડો, ${sizeUnit} આપમેળે લાગશે), તે દરેક સબ આઈટમ માં આવશે અને દરેક સબ આઈટમ માં દરેક સાઈઝ ની કટીંગ લંબાઈ અને ભાવ અલગ દાખલ થશે`}
      </p>
    )
  ) : (
    droppedNote || (
      <p className="text-xs text-gray-500">
        {fields.length > 0
          ? withCutLength
            ? "દરેક સાઈઝ ની કટીંગ લંબાઈ અને ભાવ વૈકલ્પિક છે, ખાલી રાખી શકાય"
            : "દરેક સાઈઝ ના ભાવ વૈકલ્પિક છે, ખાલી રાખી શકાય"
          : withCutLength
          ? `આઈટમ અલગ અલગ સાઈઝ માં બનતી હોય તો સાઈઝ પસંદ કરો અથવા નવી લખો (ફક્ત આંકડો, ${sizeUnit} આપમેળે લાગશે), દરેક સાઈઝ ની કટીંગ લંબાઈ અને ભાવ અલગ દાખલ થશે`
          : `આઈટમ અલગ અલગ સાઈઝ માં બનતી હોય તો સાઈઝ પસંદ કરો અથવા નવી લખો (ફક્ત આંકડો, ${sizeUnit} આપમેળે લાગશે), દરેક સાઈઝ ના ભાવ અલગ દાખલ થશે`}
      </p>
    )
  );

  // A cut length box: the item's own, or one size's.
  const lengthField = (
    name: OwnLengthPath | SizeLengthPath,
    inputId: string,
    ariaLabel?: string
  ) => (
    <Controller
      control={control}
      name={name}
      rules={{ validate: (value) => validCutLength(value) || cutLengthError }}
      render={({ field: length, fieldState }) => (
        <>
          <LengthInput
            ref={length.ref}
            id={inputId}
            name={length.name}
            value={length.value}
            unit={sizeUnit}
            placeholder="દા.ત. 96"
            aria-label={ariaLabel}
            hasError={!!fieldState.error}
            onChange={length.onChange}
            onBlur={length.onBlur}
          />
          {fieldState.error && (
            <p className={fieldErrorTextClass}>{fieldState.error.message}</p>
          )}
        </>
      )}
    />
  );

  return (
    <div className={cx("space-y-4", className)}>
      {inheritSizes ? (
        droppedNote
      ) : (
        <div className="space-y-2">
          <label htmlFor={`${idPrefix}-sizes`} className={fieldLabelClass}>
            સાઈઝ (વૈકલ્પિક)
          </label>
          {/* Enter on text that can't be added would otherwise save the item. */}
          <div
            className="relative"
            onKeyDown={(event) => {
              if (event.key === "Enter") event.preventDefault();
            }}
          >
            <LuRuler className={formSelectIconClass} />
            <CreatableSelect
              inputId={`${idPrefix}-sizes`}
              isMulti
              value={fields.map((size) => toOption(size.name, sizeUnit))}
              options={isFull ? [] : options}
              components={{ Input: SizeInput }}
              onChange={(_value, meta) =>
                handleChange(meta as ActionMeta<SizeOption>)
              }
              // Digits and one dot only, with up to 2 decimals, like the rate boxes; pasted "94 mm" leaves 94.
              onInputChange={(input) => sanitizeRate(input)}
              isValidNewOption={(input) => {
                const name = typedSizeName(input);
                const key = sizeKey(name, sizeUnit);
                return (
                  !!name &&
                  !isFull &&
                  !pickedKeys.has(key) &&
                  !optionKeys.has(key)
                );
              }}
              filterOption={(option, input) =>
                !!(option.data as SizeOption).__isNew__ ||
                option.value.includes(searchText(input))
              }
              formatCreateLabel={(input) =>
                `નવી સાઈઝ ઉમેરો: "${sizeLabel(typedSizeName(input), sizeUnit)}"`
              }
              noOptionsMessage={({ inputValue }) => {
                const name = typedSizeName(inputValue);

                if (isFull) return `વધુમાં વધુ ${maxItemSizes} સાઈઝ ઉમેરી શકાય`;
                if (name && pickedKeys.has(sizeKey(name, sizeUnit)))
                  return "આ સાઈઝ પહેલેથી ઉમેરેલી છે";
                if (rateNumber(inputValue) === 0) return "0 થી મોટી સાઈઝ લખો";
                return "સાઈઝ નંબર માં લખો, દા.ત. 96";
              }}
              placeholder="સાઈઝ પસંદ કરો અથવા નંબર લખો"
              // One keystroke or click shouldn't drop sizes together with their rates.
              isClearable={false}
              backspaceRemovesValue={false}
              closeMenuOnSelect={false}
              styles={iconMultiSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          {selectNote}
        </div>
      )}

      {!withRates ? null : fields.length === 0 ? (
        // Equal columns: the cut length first, then the three rates.
        <div
          className={cx(
            "grid grid-cols-1 gap-4",
            withCutLength ? "md:grid-cols-4" : "md:grid-cols-3"
          )}
        >
          {withCutLength && (
            <div className="space-y-2">
              <label
                htmlFor={`${idPrefix}-cut_length`}
                className={fieldLabelClass}
              >
                {cutLengthLabel} (વૈકલ્પિક)
              </label>
              {lengthField(
                `${prefix}cut_length` as OwnLengthPath,
                `${idPrefix}-cut_length`
              )}
            </div>
          )}
          <ItemRateFields
            control={control}
            prefix={prefix}
            idPrefix={idPrefix}
          />
        </div>
      ) : (
        <div className={sizeTableClass}>
          <div className={sizeTableHeadClass}>
            <div className={cx(tableHeadCellClass, "text-sm md:col-span-3")}>
              સાઈઝ
            </div>
            {withCutLength && (
              <div className={cx(tableHeadCellClass, "text-sm md:col-span-3")}>
                {cutLengthLabel} (વૈકલ્પિક)
              </div>
            )}
            {processTypes.map((type) => (
              <div
                key={type.value}
                className={cx(tableHeadCellClass, "text-sm", rateSpan)}
              >
                {rateLabel(type)} (વૈકલ્પિક)
              </div>
            ))}
          </div>

          {/* Laid out like the size rows below. Its cut length cell has no box, since lengths differ from size to
              size; from the second sub item on it has the tick that copies the sub item above's lengths. */}
          <div className={sizeTableCommonRowClass}>
            <div className="flex min-w-0 items-start gap-2 md:col-span-3">
              <LuLayers className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#34699A]" />
              <div className="min-w-0">
                <div className="font-semibold text-[#113F67]">બધી સાઈઝ</div>
                <p className="text-xs text-gray-500">
                  અહીં લખેલો ભાવ દરેક સાઈઝ માં ભરાશે
                </p>
              </div>
            </div>

            {withCutLength &&
              (canCopy ? (
                <label
                  htmlFor={`${idPrefix}-sizes-copy-above`}
                  className="flex min-w-0 cursor-pointer select-none items-start gap-2 md:col-span-3"
                >
                  <input
                    id={`${idPrefix}-sizes-copy-above`}
                    type="checkbox"
                    className={cx(checkboxClass, "mt-0.5")}
                    checked={copyAbove}
                    onChange={(event) => handleCopyAbove(event.target.checked)}
                  />
                  <span className="min-w-0">
                    <span className="block font-semibold text-[#113F67]">
                      ઉપર મુજબ
                    </span>
                    <span className="block text-xs text-gray-500">
                      સબ આઈટમ - {copyFromNumber} ની લંબાઈ
                    </span>
                  </span>
                </label>
              ) : (
                <div className="hidden md:col-span-3 md:block" />
              ))}

            {processTypes.map((type) => {
              const field = rateField(type.value);
              const inputId = `${idPrefix}-sizes-all-${field}`;

              return (
                <div key={type.value} className={cx("space-y-1", rateSpan)}>
                  <label
                    htmlFor={inputId}
                    className={cx(fieldLabelClass, "md:hidden")}
                  >
                    {rateLabel(type)}
                  </label>
                  <RateInput
                    id={inputId}
                    value={commonRates[field]}
                    placeholder="દા.ત. 120"
                    aria-label={`બધી સાઈઝ ${rateLabel(type)}`}
                    onChange={(text) => handleCommonRate(field, text)}
                  />
                </div>
              );
            })}
          </div>

          {fields.map((size, index) => {
            const label = sizeLabel(size.name, sizeUnit);

            return (
              <div
                key={size.id}
                className={cx(
                  sizeTableRowClass,
                  index > 0 && "border-t border-gray-200"
                )}
              >
                <div className={sizeTableSizeCellClass}>
                  <LuRuler className="h-4 w-4 flex-shrink-0 text-[#34699A]" />
                  <span className="break-words">{label}</span>
                </div>

                {withCutLength && (
                  <div className="space-y-1 md:col-span-3">
                    <label
                      htmlFor={`${idPrefix}-size-${size.id}-cut_length`}
                      className={cx(fieldLabelClass, "md:hidden")}
                    >
                      {cutLengthLabel}
                    </label>
                    {lengthField(
                      `${sizesName}.${index}.cut_length` as SizeLengthPath,
                      `${idPrefix}-size-${size.id}-cut_length`,
                      `${label} ${cutLengthLabel}`
                    )}
                  </div>
                )}

                {/* On phones each rate sits under the size name with its own label. */}
                {processTypes.map((type) => {
                  const field = rateField(type.value);
                  const inputId = `${idPrefix}-size-${size.id}-${field}`;

                  return (
                    <div
                      key={type.value}
                      className={cx("space-y-1", rateSpan)}
                    >
                      <label
                        htmlFor={inputId}
                        className={cx(fieldLabelClass, "md:hidden")}
                      >
                        {rateLabel(type)}
                      </label>
                      <Controller
                        control={control}
                        name={`${sizesName}.${index}.${field}` as SizeRatePath}
                        render={({ field: rate }) => (
                          <RateInput
                            ref={rate.ref}
                            id={inputId}
                            name={rate.name}
                            value={rate.value}
                            placeholder="દા.ત. 120"
                            aria-label={`${label} ${rateLabel(type)}`}
                            onChange={rate.onChange}
                            onBlur={rate.onBlur}
                          />
                        )}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// The sub item rows re-render on every keystroke, and all of these props stay the same.
export default React.memo(ItemSizeRates);
